import {
	Box,
	Menu,
	MenuItem,
	Tooltip,
	Divider,
	ListItemIcon,
	ListItemText,
} from "@mui/material";

import {
	PermissionActionsEnum,
	PermissionRolesEnum,
} from "../../../shared/enums/profiles";
import { checkByProfileGuard } from "../../../shared/guards/ProfileGuard";

import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import BlockOutlinedIcon from "@mui/icons-material/BlockOutlined";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";

import * as React from "react";
import { FireQuotationData } from "../Quotation/components/types/fire";

// Ações disponíveis
export type FireMenuAction =
	| "details"
	| "dados"
	| "cancel"
	| "downloadPDF";

interface Props {
	anchorEl: HTMLElement | null;
	open: boolean;
	onClose: () => void;
	selectedRow: FireQuotationData | null;
	onSelect: (action: FireMenuAction) => void;
}

// ===== Normalização de status =====
type FireStatusNorm = "pending" | "hired" | "canceled" | "other";

const norm = (s?: string | null) => (s ?? "").trim().toLowerCase();

function normalizeStatus(raw?: string | null): FireStatusNorm {
	const n = norm(raw);
	if (n === "pending" || n === "open") return "pending";
	if (n === "hired" || n === "contratado") return "hired";
	if (n === "canceled" || n === "cancelado") return "canceled";
	return "other";
}

// ===== Lógica de permissões (SEM contratar) =====
function computePermissions(row: FireQuotationData | null) {
	if (!row) {
		const reason = "Nenhum orçamento selecionado.";
		return {
			canEdit: false,
			reasonEdit: reason,
			canCancel: false,
			reasonCancel: reason,
			canDownload: false,
			reasonDownload: reason,
		};
	}

	const status = normalizeStatus(row.status);

	// 1. EDITAR
	// Permissão de role
	const hasEditPermission = checkByProfileGuard(
		PermissionRolesEnum.FireOptions,
		PermissionActionsEnum.Update
	);
	// Regra de negócio
	const isStatusEditable = status !== "hired" && status !== "canceled";

	let canEdit = true;
	let reasonEdit = "";

	if (!hasEditPermission) {
		canEdit = false;
		reasonEdit = "Você não tem permissão para editar orçamentos.";
	} else if (!isStatusEditable) {
		canEdit = false;
		reasonEdit = `Não é possível editar um orçamento em status "${status}".`;
	}

	// 2. CANCELAR
	// Permissão de role
	const hasCancelPermission = checkByProfileGuard(
		PermissionRolesEnum.FireOptions,
		PermissionActionsEnum.Cancel
	);
	// Regra de negócio
	const isStatusCancelable = status !== "hired" && status !== "canceled";

	let canCancel = true;
	let reasonCancel = "";

	if (!hasCancelPermission) {
		canCancel = false;
		reasonCancel = "Você não tem permissão para cancelar orçamentos.";
	} else if (!isStatusCancelable) {
		canCancel = false;
		reasonCancel = `O orçamento já está "${status}".`;
	}

	// 3. PDF (Imprimir)
	// Permissão de role
	const hasPrintPermission = checkByProfileGuard(
		PermissionRolesEnum.FireOptions,
		PermissionActionsEnum.Print
	);

	let canDownload = true;
	let reasonDownload = "";

	if (!hasPrintPermission) {
		canDownload = false;
		reasonDownload = "Você não tem permissão para imprimir/baixar PDF.";
	}

	return {
		canEdit,
		reasonEdit,
		canCancel,
		reasonCancel,
		canDownload,
		reasonDownload,
	};
}

// Tooltip que só aparece quando desabilitado
const TooltipIfDisabled: React.FC<{
	title: string;
	disabled: boolean;
	children: React.ReactNode;
}> = ({ title, disabled, children }) =>
		disabled ? (
			<Tooltip title={title}>
				<span style={{ display: "block" }}>{children}</span>
			</Tooltip>
		) : (
			<>{children}</>
		);

// ===== COMPONENTE FINAL =====
export const FireMenu: React.FC<Props> = ({
	anchorEl,
	open,
	onClose,
	selectedRow,
	onSelect,
}) => {
	const {
		canEdit,
		reasonEdit,
		canCancel,
		reasonCancel,
		canDownload,
		reasonDownload,
	} = computePermissions(selectedRow);

	const handle = (action: FireMenuAction) => {
		if (selectedRow) onSelect(action);
		onClose();
	};

	return (
		<Menu
			anchorEl={anchorEl}
			open={open}
			onClose={onClose}
			PaperProps={{
				sx: {
					borderRadius: 2,
					minWidth: 230,
					boxShadow: 4,
					py: 0.5,
				},
			}}
		>
			<Box>

				{/* Detalhes */}
				<MenuItem onClick={() => handle("details")}>
					<ListItemIcon>
						<VisibilityOutlinedIcon fontSize="small" />
					</ListItemIcon>
					<ListItemText primary="Detalhes do Orçamento" />
				</MenuItem>

				{/* Editar */}
				<TooltipIfDisabled title={reasonEdit} disabled={!canEdit}>
					<MenuItem disabled={!canEdit} onClick={() => handle("dados")}>
						<ListItemIcon>
							<EditOutlinedIcon fontSize="small" />
						</ListItemIcon>
						<ListItemText primary="Atualizar Orçamento" />
					</MenuItem>
				</TooltipIfDisabled>

				<Divider />

				{/* Baixar PDF */}
				<TooltipIfDisabled title={reasonDownload} disabled={!canDownload}>
					<MenuItem disabled={!canDownload} onClick={() => handle("downloadPDF")}>
						<ListItemIcon>
							<PictureAsPdfOutlinedIcon fontSize="small" />
						</ListItemIcon>
						<ListItemText primary="Baixar PDF" />
					</MenuItem>
				</TooltipIfDisabled>

				<Divider />

				{/* Cancelar */}
				<TooltipIfDisabled title={reasonCancel} disabled={!canCancel}>
					<MenuItem disabled={!canCancel} onClick={() => handle("cancel")}>
						<ListItemIcon>
							<BlockOutlinedIcon fontSize="small" />
						</ListItemIcon>
						<ListItemText primary="Cancelar" />
					</MenuItem>
				</TooltipIfDisabled>

			</Box>
		</Menu>
	);
};
